/**
 * Change 19 §13 — *"design and test the platform for worst-case scenarios, not
 * only average or ideal usage conditions."*
 *
 * 🔴 THE WORST CASE THIS ADDRESSES IS ONE DROPPED PACKET ON A SCHOOL'S WIFI.
 *
 * `client.ts` already classifies every failure into "the moment was wrong" and
 * "the request was wrong", in a function whose own header calls it *"the most
 * consequential function in the offline stack"* — and until now `isRetryable`
 * was called by nothing but its own test. Every dashboard read gave up on the
 * first failure and left a teacher looking at an error with a Retry button, on a
 * connection where the second attempt would have worked.
 *
 * 🔴 IT IS A SCHEDULE, NOT A LOOP, AND THAT IS THE WHOLE DESIGN. Backoff written
 * inline inside a hook is backoff nobody can test without a component, a fake
 * clock and an abort controller. This is a pure function from attempt number to
 * milliseconds; the hook does the waiting.
 */

/**
 * 🔴 TWO RETRIES, AND NO MORE. This is a screen somebody is looking at, not an
 * outbox — the outbox worker has its own policy and may keep a message for days
 * (`outbox.policy.ts`). Here, a third attempt means a teacher stares at a
 * spinner for the better part of ten seconds before being told anything, and a
 * spinner that never resolves is worse than an error with a button.
 */
export const MAX_RETRIES = 2

/** The first wait. Doubling from here. */
export const BASE_DELAY_MS = 400

/**
 * 🔴 JITTER IS NOT OPTIONAL AND THE REASON IS SPECIFIC TO THIS PRODUCT. A class
 * of thirty tablets on one school access point all lose connectivity at the same
 * instant when the router blips, and all retry at the same instant afterwards —
 * a thundering herd against an API that has just come back up, from the devices
 * least able to absorb a second failure. A random fraction spreads them.
 *
 * ⚠️ IT IS FULL JITTER — the delay is a random point in `[0, ceiling]` rather
 * than `ceiling ± a bit`. Half-jitter still clusters, which is the failure mode
 * the herd produces.
 */
export function retryDelayMs(attempt: number, random: () => number = Math.random): number {
  if (attempt < 1) return 0
  const ceiling = BASE_DELAY_MS * 2 ** (attempt - 1)
  /* At least a tick, so a `random()` of 0 is still a yield rather than a
     synchronous re-entry into the same failing call. */
  return Math.max(1, Math.floor(random() * ceiling))
}

/**
 * Should this failure be tried again, and how many times has it been?
 *
 * ⚠️ IT TAKES THE CLASSIFIER RATHER THAN IMPORTING IT, so the schedule can be
 * tested without an `ApiError` and so the one definition of "transient" stays in
 * `client.ts`, next to the code that produced the status.
 */
export function shouldRetry(
  error: unknown,
  attemptsSoFar: number,
  isRetryable: (e: unknown) => boolean,
  max = MAX_RETRIES,
): boolean {
  if (attemptsSoFar >= max) return false
  return isRetryable(error)
}
